import PropTypes from "prop-types";

import { Box, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { PurchaseOrderOrderTypeEnum } from "@/features/purchase-orders";
import { formatNumber } from "@/features/shared";
import {
  DownstreamTransactionLinks,
  ProductVariantNameCell,
  UpstreamTransactionLink,
} from "@/features/ui";

export const ProductVariantsSection = ({ purchaseInvoiceProductVariants }) => {
  const { hasPermission } = useAuth();

  if (!purchaseInvoiceProductVariants?.length) {
    return null;
  }

  const columns = [
    {
      field: "name",
      headerName: "Produk Varian",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} linkToStock />
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      flex: 1,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",
      flex: 1,
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
    {
      field: "purchaseOrder",
      headerName: "Pesanan",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row: { purchaseOrder } }) => {
        // Determine which permission to check based on order type
        const hasReadPermission =
          purchaseOrder?.orderType === PurchaseOrderOrderTypeEnum.LAB
            ? hasPermission(PermissionEnum.READ_PURCHASE_ORDERS_LAB)
            : hasPermission(PermissionEnum.READ_PURCHASE_ORDERS_STANDARD);

        return (
          <UpstreamTransactionLink
            referenceNumber={purchaseOrder?.referenceNumber}
            quantity={purchaseOrder?.quantity}
            orderType={purchaseOrder?.orderType}
            clickable={hasReadPermission}
          />
        );
      },
    },
    {
      field: "goodsReceipt",
      headerName: "Penerimaan",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row: { goodsReceipt } }) => (
        <UpstreamTransactionLink
          referenceNumber={goodsReceipt?.referenceNumber}
          quantity={goodsReceipt?.quantity}
          clickable={hasPermission(PermissionEnum.READ_GOODS_RECEIPTS)}
        />
      ),
    },
    {
      field: "purchaseReturns",
      headerName: "Retur",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.purchaseReturns}
          clickable={hasPermission(PermissionEnum.READ_PURCHASE_RETURNS)}
        />
      ),
    },
    {
      field: "price",
      headerName: "Harga",
      type: "number",
      flex: 1.5,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "totalPrice",
      headerName: "Total",
      flex: 2,
      type: "number",
      valueGetter: (_, row) => row.quantity * row.price - row.discount,
      renderCell: ({ row }) => {
        const { quantity, price, discount } = row;
        const total = quantity * price - discount;
        const hasDiscount = discount && discount > 0;

        if (!hasDiscount) {
          return (
            <Typography variant="body2">{formatNumber(total)}</Typography>
          );
        }

        const originalTotal = quantity * price;
        return (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatNumber(total)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textDecoration: "line-through",
                color: "text.secondary",
                fontSize: "0.7rem",
              }}
            >
              {formatNumber(originalTotal)}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: "note",
      headerName: "Catatan",
      flex: 1.5,
      valueGetter: (_, row) => row.note || "-",
    },
  ];

  return (
    <Stack spacing={2}>
      <DataGrid
        getRowHeight={() => "auto"}
        columns={columns}
        rows={purchaseInvoiceProductVariants}
        disableRowSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
      />
    </Stack>
  );
};

ProductVariantsSection.propTypes = {
  purchaseInvoiceProductVariants: PropTypes.array.isRequired,
};
